import { Signal, signal } from "@angular/core";
import { Choice, Component, Customization, FlattenCustomization, ParentPathCustomization, ProductDetailsResponse } from "../../../../../models/order/api-models/order-pd-response.model";
import { AdditionalOfferData, OfferType } from "../../../../../models/order/workflow/additional-offer.model";
import { OrderFlowType } from "../../../../../models/order/order.model";

export abstract class BasePdResponseService {
    protected customizations = signal<FlattenCustomization[]>([]);
    abstract productResponse: Signal<ProductDetailsResponse>;
    abstract setupCustomizations(pdResponse: ProductDetailsResponse): void;
    protected isIncludedOffer(cust: FlattenCustomization): boolean {
        return cust.customization.SubGroupName === "IncludedOffers" && cust.customization.CustomizationType === "Selection"
    }

    protected isAbpOffer(cust: FlattenCustomization): boolean {
        return cust.customization.SubGroupName === "ABP" || cust.customization.SubGroupName === "WirelessABP";
    }

    protected FlowVisibleQuery(cust: FlattenCustomization): boolean {
        const hasVisibleChoice = cust.customization.Choices?.Choice?.some(choice => choice.FlowVisible?.toLocaleLowerCase() === 'true');
        return cust.customization.FlowVisible?.toLocaleLowerCase() === 'true' || hasVisibleChoice;
    }

    protected FlowNotVisibleQuery(cust: FlattenCustomization): boolean {
        const hasVisibleChoice = cust.customization.Choices?.Choice?.some(choice => choice.FlowVisible?.toLocaleLowerCase() === 'true');
        return !(cust.customization.FlowVisible?.toLocaleLowerCase() === 'true' || hasVisibleChoice);
    }

    protected CartVisibleQuery(cust: FlattenCustomization): boolean {
        const hasVisibleChoice = cust.customization.Choices?.Choice?.some(choice => choice.CartVisible?.toLocaleLowerCase() === 'true');
        return cust.customization.CartVisible?.toLocaleLowerCase() === 'true' || hasVisibleChoice;
    }

    getCustomizations = this.customizations.asReadonly();

    getIncludedOffersCustomizations(): FlattenCustomization[] {
        const customizations = this.customizations();
        return customizations.filter(this.isIncludedOffer);
    }

    getFlowVisibleCustomizations(): FlattenCustomization[] {
        const customizations = this.customizations();
        return customizations.filter(this.FlowVisibleQuery);
    }

    getCartVisibleCustomizations(): FlattenCustomization[] {
        const customizations = this.customizations();
        return customizations.filter(this.CartVisibleQuery);
    }

    getDefaultSelectedChoices(): AdditionalOfferData[] {
        const pdResponse = this.productResponse();
        if (pdResponse) this.setupCustomizations(pdResponse);
        const allCustomizations = this.getCustomizations();
        const includedOfferCustomizations = allCustomizations.filter(this.isIncludedOffer);
        const defaultChoices = includedOfferCustomizations.map(cust => cust.customization.Choices?.Choice[0]);

        const pdDefaultOffers: AdditionalOfferData[] = []
        for (const offer of defaultChoices) {
            const customization = this.findCustomizationByChoiceCode(offer.ChoiceCode);
            const type = customization?.customization.SubGroupName === 'IncludedOffers' ? OfferType.Required : OfferType.Additional;
            pdDefaultOffers.push({ customizationCode: customization?.customization.CustomizationCode, choiceCode: offer.ChoiceCode, type, usoc: offer.Usoc });
        }
        return pdDefaultOffers;
    }

    findCustomizationByChoiceCode(choiceCode: string): FlattenCustomization | undefined {
        const customizations = this.customizations();
        return customizations.find(cust => cust.customization.Choices?.Choice?.some(choice => choice.ChoiceCode === choiceCode));
    }

    findChoiceCode(choiceCode: string): Choice | null {
        const customizations = this.customizations();
        for (const cust of customizations) {
            const found = cust.customization.Choices?.Choice?.find(choice => choice.ChoiceCode === choiceCode);
            if (found) return found;
        }
        return null;
    }

    findByCustomizationCode(customizationCode: string): FlattenCustomization | undefined {
        const customizations = this.customizations();
        return customizations.find(cust => cust.customization.CustomizationCode === customizationCode);
    }

    findPDChoiceByCode(choiceCode: string, productType: string): { choiceCode: string, parentCode: string } | null {
        const pdComponent = this.productResponse().Components?.Component.find(c => c.ProductType === productType);
        if (pdComponent) {
            const recursiveSearch = (customization: Customization, parentCode: string): { choiceCode: string, parentCode: string } => {
                parentCode; // Currently not used, but can be utilized if needed
                if (customization.Choices?.Choice?.length > 0) {
                    // Search in choices
                    for (const choice of customization.Choices.Choice) {
                        if (choice.ChoiceCode === choiceCode) {
                            return { choiceCode, parentCode: customization.CustomizationCode };
                        }

                        if (choice.Customizations?.Customization?.length > 0) {
                            for (const nestedCust of choice.Customizations.Customization) {
                                const result = recursiveSearch(nestedCust, choice.ChoiceCode);
                                if (result) return result;
                            }
                        }
                    }
                }
                return null;
            };

            for (const cust of pdComponent.Customizations?.Customization || []) {
                const result = recursiveSearch(cust, pdComponent.ComponentCode);
                if (result) return result;
            }
        }
        return null;
    }

    protected findNestedCustomization(customizations: Customization[], customizationCode: string): Customization | undefined {
        for (const cust of customizations) {
            if (cust.CustomizationCode === customizationCode) {
                return cust;
            }

            if (cust.Choices?.Choice) {
                for (const choice of cust.Choices.Choice) {
                    if (choice.Customizations?.Customization) {
                        const found = this.findNestedCustomization(choice.Customizations.Customization, customizationCode);
                        if (found) {
                            return found;
                        }
                    }
                }
            }
        }
        return undefined;
    }

    protected flattenCustomizations(data: Component, category: OrderFlowType ): FlattenCustomization[] {
        if (!data) return [];
        const result: FlattenCustomization[] = [];

        function traverse(customizations: Customization[] = [], path: ParentPathCustomization[] = [], parentChoice?: any) {
            parentChoice; // Currently not used, but can be utilized if needed
            for (const cust of customizations) {
                let hasNested = false;

                const choices = cust.Choices?.Choice ?? [];

                // Check for nested customizations
                for (const choice of choices) {
                    const nested = choice.Customizations?.Customization ?? [];
                    if (nested.length > 0) {
                        hasNested = true;
                        const currentPath = [...path]; // Add current to path
                        currentPath.push({ customization: cust, type: 'customization' });
                        currentPath.push({ customization: choice, type: 'choice' });
                        traverse(nested, currentPath, choice); // Pass choice as parent
                    }
                }

                // If no nested Customizations at this level → collect with path
                if (!hasNested && cust.Choices?.Choice?.length > 0) {
                    cust.Choices.Choice.sort((a, b) => (a.Seq ?? Number.MAX_SAFE_INTEGER) - (b.Seq ?? Number.MAX_SAFE_INTEGER));
                    if (cust.Category === category) {
                        result.push({
                            customization: cust,
                            path: path,
                        });
                    }
                }
            }
        }

        traverse(data.Customizations?.Customization);
        result.sort((a, b) => (a.customization.Seq ?? Number.MAX_SAFE_INTEGER) - (b.customization.Seq ?? Number.MAX_SAFE_INTEGER));
        return result;
    }

    hasAcceptDeclineChoices(cust: FlattenCustomization): boolean {
        return cust.customization.Choices?.Choice?.some(choice => choice.Name.toLocaleLowerCase().indexOf('decline offer') !== -1 && choice.CartVisible?.toLocaleLowerCase() === 'false') ?? false;
    }

}
